import { useEffect, useState } from 'react';
import { PackageCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api';

/**
 * Posledné aktualizácie systému.
 *
 * Popis zmien nesie samotný balík (`verzia.json`) a aktualizátor si ho odloží.
 * Bez tohto by po pol roku nikto nevedel, čo v systéme pribudlo a odkedy sa
 * niečo správa inak – zálohy hovoria len to, kedy sa niečo nasadilo.
 */

const MESIACE = ['januára', 'februára', 'marca', 'apríla', 'mája', 'júna',
  'júla', 'augusta', 'septembra', 'októbra', 'novembra', 'decembra'];

/** „22. augusta“ pri tohtoročných, s rokom pri starších. */
function datum(iso) {
  const d = new Date(String(iso).replace(' ', 'T') + (String(iso).includes('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return '';
  const rok = d.getFullYear() === new Date().getFullYear() ? '' : ` ${d.getFullYear()}`;
  return `${d.getDate()}. ${MESIACE[d.getMonth()]}${rok}`;
}

/* Koľko aktualizácií je vidieť hneď. Zvyšok si vyžiada človek tlačidlom –
 * zoznam zmien je dlhý a dlaždica by inak zatlačila zvyšok prehľadu dolu. */
const NA_ZACIATOK = 3;

export default function Aktualizacie() {
  const [v, setV] = useState(null);
  const [vsetko, setVsetko] = useState(false);

  useEffect(() => {
    api.get('/api/stats/aktualizacie').then(setV).catch(() => setV({ polozky: [] }));
  }, []);

  if (!v) return null;
  const vsetkyPolozky = v.polozky || [];
  const polozky = vsetko ? vsetkyPolozky : vsetkyPolozky.slice(0, NA_ZACIATOK);
  const skryte = vsetkyPolozky.length - polozky.length;

  return (
    <div className="card-p">
      <div className="mb-3 flex items-center gap-2">
        <PackageCheck size={16} className="text-slate-400" />
        <h2 className="font-semibold text-slate-700">Posledné aktualizácie</h2>
      </div>

      {!polozky.length ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Zatiaľ tu nič nie je. Prvý nahratý balík sa zapíše sem.
        </p>
      ) : (
        <ol className="space-y-3">
          {polozky.map((z, i) => (
            <li key={z.id} className="flex gap-3">
              {/* Farebná bodka namiesto odrážky – najnovšia je zvýraznená. */}
              <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${i === 0 ? 'bg-brand-600' : 'bg-slate-300'}`} />

              <div className="min-w-0 flex-1 pb-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-medium text-slate-800">
                    {z.title || z.version || 'Aktualizácia systému'}
                  </span>
                  {z.subor && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">{z.subor}</span>}
                  <span className="text-xs text-slate-400">{datum(z.applied_at)}</span>
                </div>

                {z.changes?.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                    {z.changes.map((c, k) => (
                      <li key={k} className="flex gap-1.5">
                        <span className="text-slate-300">·</span>
                        <span className="min-w-0">{c}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {z.applied_by && (
                  <div className="mt-0.5 text-[11px] text-slate-400">nasadil {z.applied_by}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {(skryte > 0 || vsetko) && vsetkyPolozky.length > NA_ZACIATOK && (
        <button
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-slate-200 py-1.5 text-xs text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          onClick={() => setVsetko((x) => !x)}>
          {vsetko
            ? <><ChevronUp size={13} /> Zobraziť menej</>
            : <><ChevronDown size={13} /> Ukázať viac ({skryte})</>}
        </button>
      )}

      {v.zdroj === 'zalohy' && polozky.length > 0 && (
        <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] text-slate-400">
          Zoznam je zatiaľ poskladaný zo záloh – čo presne sa menilo, bude vidieť
          pri ďalšej aktualizácii.
        </p>
      )}
    </div>
  );
}
